import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Mail, Phone, ArrowUpRight } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { Reveal, Overline } from "./Reveal";
import { COMPANY, IMAGES } from "@/i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Contact() {
  const { t } = useLang();
  const [form, setForm] = useState({ name: "", email: "", company: "", message: "" });
  const [loading, setLoading] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) return;
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success(t.contact.success);
      setForm({ name: "", email: "", company: "", message: "" });
    } catch (err) {
      toast.error(t.contact.error);
    } finally {
      setLoading(false);
    }
  };

  const inputCls =
    "w-full bg-transparent border-0 border-b border-line focus:border-brand text-ink placeholder-ink-muted py-4 outline-none transition-colors duration-300 text-base";

  return (
    <section id="contact" className="relative pt-28 md:pt-40">
      <div className="pointer-events-none absolute bottom-0 left-1/2 -translate-x-1/2 h-[600px] w-[900px] rounded-full bg-brand/10 blur-[140px]" />
      <div className="relative mx-auto max-w-[1400px] px-6 lg:px-10">
        <Reveal><Overline>{t.contact.overline}</Overline></Reveal>
        <Reveal delay={0.05}>
          <h2 className="font-head font-black tracking-tighter text-ink text-[clamp(2.5rem,7vw,6.5rem)] leading-[0.9] mt-5 max-w-4xl">
            {t.contact.title}
          </h2>
        </Reveal>

        <div className="grid lg:grid-cols-12 gap-14 lg:gap-20 mt-16">
          <Reveal delay={0.1} className="lg:col-span-7">
            <p className="text-base md:text-lg text-ink-2 leading-relaxed max-w-xl mb-10">{t.contact.subtitle}</p>
            <form onSubmit={submit} className="space-y-7" data-testid="contact-form">
              <div className="grid sm:grid-cols-2 gap-7">
                <input
                  type="text" required value={form.name} onChange={update("name")}
                  placeholder={t.contact.name} className={inputCls} data-testid="contact-name"
                />
                <input
                  type="email" required value={form.email} onChange={update("email")}
                  placeholder={t.contact.email} className={inputCls} data-testid="contact-email"
                />
              </div>
              <input
                type="text" value={form.company} onChange={update("company")}
                placeholder={t.contact.company} className={inputCls} data-testid="contact-company"
              />
              <textarea
                required rows={4} value={form.message} onChange={update("message")}
                placeholder={t.contact.message} className={`${inputCls} resize-none`} data-testid="contact-message"
              />
              <button
                type="submit" disabled={loading}
                className="group inline-flex items-center gap-2 bg-brand hover:bg-brand-hover disabled:opacity-60 text-white font-medium rounded-full px-8 py-4 transition-colors duration-300 active:scale-95"
                data-testid="contact-submit"
              >
                {loading ? t.contact.sending : t.contact.submit}
                <ArrowUpRight className="h-5 w-5 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform duration-300" />
              </button>
            </form>
          </Reveal>

          <Reveal delay={0.15} className="lg:col-span-5">
            <div className="font-mono text-xs tracking-widest uppercase text-ink-muted mb-6">{t.contact.directTitle}</div>
            <div className="space-y-5">
              <a href={`mailto:${COMPANY.email}`} className="flex items-center gap-4 group" data-testid="contact-email-link">
                <span className="h-11 w-11 rounded-full border border-line group-hover:border-brand flex items-center justify-center text-brand transition-colors duration-300">
                  <Mail className="h-5 w-5" />
                </span>
                <span className="text-lg text-ink group-hover:text-brand transition-colors duration-300">{COMPANY.email}</span>
              </a>
              <a href={`tel:${COMPANY.phone.replace(/\s/g, "")}`} className="flex items-center gap-4 group" data-testid="contact-phone-link">
                <span className="h-11 w-11 rounded-full border border-line group-hover:border-brand flex items-center justify-center text-brand transition-colors duration-300">
                  <Phone className="h-5 w-5" />
                </span>
                <span className="text-lg text-ink group-hover:text-brand transition-colors duration-300">{COMPANY.phone}</span>
              </a>
            </div>
            <div className="mt-10 pt-8 border-t border-line text-sm text-ink-muted space-y-1.5 font-mono">
              <div>{COMPANY.name}</div>
              <div>{COMPANY.kvk}</div>
              <div>{COMPANY.vat}</div>
            </div>
          </Reveal>
        </div>

        {/* Footer */}
        <footer className="mt-28 md:mt-40 border-t border-line py-12 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
          <div className="flex items-center gap-4">
            <img src={IMAGES.logoFull} alt="CHEPL — Check-in with people" className="h-16 w-auto object-contain" />
            <div className="text-xs text-ink-muted max-w-[180px]">{t.footer.built}</div>
          </div>
          <div className="font-mono text-xs tracking-widest uppercase text-brand">{t.hero.brandline}</div>
          <div className="flex flex-col md:items-end gap-1.5">
            <Link
              to="/privacy"
              className="text-xs text-ink-2 hover:text-brand transition-colors duration-300"
              data-testid="footer-privacy-link"
            >
              {t.footer.privacy}
            </Link>
            <div className="text-xs text-ink-muted">© {new Date().getFullYear()} {COMPANY.name}. {t.footer.rights}</div>
          </div>
        </footer>
      </div>
    </section>
  );
}
